import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Stamp as StampIcon, Loader2, Trash2, Share2, Mail } from 'lucide-react';
import { toast } from 'sonner';

export function CategoryStampsManager({ eventId, waypoints, stamps, onChange }) {
  const [busy, setBusy] = useState(null);
  const cats = [...new Map((waypoints || []).filter((w) => w.category && w.waypoint_type !== 'info').map((w) => [w.category.toUpperCase(), w.color])).entries()].sort();

  const upload = async (cat, file) => {
    if (!file) return;
    setBusy(cat);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/admin/events/${eventId}/category-stamps/${encodeURIComponent(cat)}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange({ ...(stamps || {}), [cat]: r.data.url });
      toast.success(`Sello ${cat} guardado`);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo subir el sello'); } finally { setBusy(null); }
  };
  const remove = async (cat) => {
    setBusy(cat);
    try {
      await axios.delete(`${API}/admin/events/${eventId}/category-stamps/${encodeURIComponent(cat)}`);
      const next = { ...(stamps || {}) }; delete next[cat]; onChange(next);
    } catch { toast.error('No se pudo eliminar'); } finally { setBusy(null); }
  };

  return (
    <div className="p-3 bg-rose-50 rounded-lg border border-rose-200 space-y-2" data-testid="category-stamps-manager">
      <p className="text-sm font-semibold text-gray-900 flex items-center gap-1"><StampIcon className="w-4 h-4 text-rose-600" /> Sellos por categoría</p>
      <p className="text-xs text-gray-600">Sube una ilustración (PNG con fondo transparente, cuadrada) por categoría. Se mostrará en el mapa y en la cuadrícula del pasaporte cuando el piloto acredite un waypoint. Sin sello, se usa uno genérico con el color de la categoría.</p>
      {!cats.length && <p className="text-xs text-amber-700">Importa primero los waypoints con categoría.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {cats.map(([cat, color]) => (
          <div key={cat} className="rounded-md bg-white border border-gray-200 p-2 flex items-center gap-2" data-testid={`stamp-row-${cat}`}>
            {stamps?.[cat]
              ? <img src={stamps[cat]} alt={cat} className="w-12 h-12 object-contain" />
              : <span className="w-12 h-12 rounded-full border-4 border-double flex items-center justify-center text-lg font-black -rotate-12" style={{ borderColor: color || '#9ca3af', color: color || '#9ca3af' }}>✓</span>}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold truncate">{cat}</p>
              <label className="text-[11px] text-blue-700 underline cursor-pointer">
                {busy === cat ? <Loader2 className="w-3 h-3 inline animate-spin" /> : stamps?.[cat] ? 'Cambiar' : 'Subir sello'}
                <input type="file" accept="image/*" className="hidden" disabled={busy === cat} onChange={(e) => upload(cat, e.target.files?.[0])} data-testid={`stamp-input-${cat}`} />
              </label>
              {stamps?.[cat] && <button type="button" onClick={() => remove(cat)} className="ml-2 text-[11px] text-red-600 underline" data-testid={`stamp-remove-${cat}`}>Quitar</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ShareTemplateUploader({ eventId, template, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/admin/events/${eventId}/share-template`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(r.data.share_template); toast.success('Plantilla para compartir guardada');
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo subir'); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await axios.delete(`${API}/admin/events/${eventId}/share-template`); onChange(null); } catch { toast.error('No se pudo eliminar'); } finally { setBusy(false); }
  };
  return (
    <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 space-y-2" data-testid="share-template-uploader">
      <p className="text-sm font-semibold text-gray-900 flex items-center gap-1"><Share2 className="w-4 h-4 text-emerald-600" /> Imagen para compartir el logro (opcional)</p>
      <p className="text-xs text-gray-600">Diseño cuadrado 1080×1080 (PNG/JPG). Si no lo subes, se recorta la plantilla del diploma. El texto (reto, piloto, puntos) se coloca en un panel centrado.</p>
      <div className="flex items-center gap-3 flex-wrap">
        <input type="file" accept="image/png,image/jpeg" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} className="text-sm" data-testid="share-template-input" />
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        {template && (<>
          <img src={template} alt="" className="w-12 h-12 object-cover rounded border" />
          <Button type="button" size="sm" variant="ghost" onClick={remove} className="h-7 text-red-600 px-2" data-testid="share-template-remove-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Quitar</Button>
        </>)}
      </div>
    </div>
  );
}

export function MonthlySummaryPanel({ eventId }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const email = window.prompt('Enviar el resumen mensual ahora. Deja vacío para TODOS los inscritos o escribe un email para una prueba:', '');
    if (email === null) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/events/${eventId}/send-monthly-summary`, null, { params: email.trim() ? { only_email: email.trim() } : {} });
      toast.success(`Resumen enviado a ${r.data.sent} piloto(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo enviar'); } finally { setBusy(false); }
  };
  return (
    <div className="p-3 bg-sky-50 rounded-lg border border-sky-200 flex items-start justify-between gap-3 flex-wrap" data-testid="monthly-summary-panel">
      <div>
        <p className="text-sm font-semibold text-gray-900 flex items-center gap-1"><Mail className="w-4 h-4 text-sky-600" /> Resumen mensual a los pilotos</p>
        <p className="text-xs text-gray-600">Se envía automáticamente el día 1 de cada mes a las 9:00 mientras el evento esté activo: waypoints del mes, acumulado, puntos, puesto, reto y días restantes.</p>
      </div>
      <Button type="button" size="sm" variant="outline" disabled={busy} onClick={send} className="h-9 border-sky-300 text-sky-800" data-testid="monthly-summary-send-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />} Enviar ahora
      </Button>
    </div>
  );
}
